package com.evoapps.evoshift.domain.leave

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.LeaveAllowance
import com.evoapps.evoshift.domain.model.LeaveBalance
import com.evoapps.evoshift.domain.model.LeaveEntitlement
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlin.math.ceil
import kotlin.math.min

/**
 * SH-113: annual leave balances, pure. The entitlement comes from the market's rules (PL: 20 or 26
 * days by seniority, employment contracts only), pro rata to the FTE and to the months of the year
 * the engagement covers (a started month counts as a whole one, a started day is rounded up). A
 * manager may set another entitlement and the carry-over from the year before
 * ([LeaveAllowance]). Used = approved annual leave and leave on demand in working days of the year
 * (weekends and the market's holidays do not count).
 *
 * Simplified on purpose: the first job's 1/12 a month rule and leave carried over from more than a
 * year before are not modelled; the manager's figures cover them.
 */
object LeaveBalances {

    /** The leave types that take days from the annual leave. */
    val LEAVE_TYPES = setOf(AbsenceType.VACATION, AbsenceType.ON_DEMAND)

    /** Working days of [start]..[end] (both included) in [year] (null: all), by [rules]. */
    fun workingDays(rules: PayrollRules, start: LocalDate, end: LocalDate, year: Int? = null): Int =
        days(start, end, year).count { rules.isLeaveDay(it) }

    /** [workingDays] by market. */
    fun workingDays(market: Market, start: LocalDate, end: LocalDate, year: Int? = null): Int =
        workingDays(PayrollRules.of(market), start, end, year)

    private fun days(start: LocalDate, end: LocalDate, year: Int?): Sequence<LocalDate> {
        val from = if (year != null) maxOf(start, LocalDate(year, 1, 1)) else start
        val to = if (year != null) minOf(end, LocalDate(year, 12, 31)) else end
        if (to < from) return emptySequence()
        return generateSequence(from) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= to }
    }

    /** Months of [year] from [startDate] to [endDate] ("YYYY-MM-DD", null: open), a started month counting whole. */
    fun monthsEngaged(startDate: String?, endDate: String?, year: Int): Int {
        val start = parse(startDate)
        val end = parse(endDate)
        val first = if (start == null || start.year < year) 1 else if (start.year > year) return 0 else start.month.number
        val last = if (end == null || end.year > year) 12 else if (end.year < year) return 0 else end.month.number
        return (last - first + 1).coerceIn(0, 12)
    }

    /** The share of full time: a salary's FTE, hourly pay's stated monthly hours over the year's average month. */
    fun fte(rules: PayrollRules, financial: FinancialEngagement, year: Int): Double =
        when (val pay = financial.compensation) {
            is CompensationModel.MonthlyFixed -> pay.fte.takeIf { it > 0 } ?: 1.0
            is CompensationModel.Hourly -> {
                val month = (1..12).sumOf { rules.workingHours(year, it) } / 12.0
                financial.declaredNumberOfHours?.takeIf { it > 0 && month > 0 }?.let { min(1.0, it / month) } ?: 1.0
            }
        }

    /**
     * The entitlement [financial]'s contract gives at [engagement] in [year], pro rata; null when
     * the contract carries no statutory leave or the terms are unknown.
     */
    fun statutoryDays(rules: PayrollRules, financial: FinancialEngagement?, engagement: Engagement?, year: Int): Int? {
        financial ?: return null
        val full = rules.annualLeaveDays(financial.contract, financial.profileOn("$year-12-31"), year) ?: return null
        val months = monthsEngaged(engagement?.startDate, engagement?.endDate, year)
        // Rounded to a millionth first, so that 26 × 0.5 stays 13 and is not pushed to 14.
        val exact = full * fte(rules, financial, year) * months / 12.0
        return ceil(kotlin.math.round(exact * 1_000_000) / 1_000_000).toInt()
    }

    /** The facts of one person's balance apart from the absences. */
    fun entitlement(
        market: Market,
        userId: String,
        workplaceId: String,
        year: Int,
        financial: FinancialEngagement?,
        engagement: Engagement?,
        allowance: LeaveAllowance?
    ): LeaveEntitlement {
        val rules = PayrollRules.of(market)
        return LeaveEntitlement(
            userId = userId,
            workplaceId = workplaceId,
            year = year,
            market = market,
            statutoryDays = statutoryDays(rules, financial, engagement, year),
            onDemandLimit = rules.onDemandLeaveDays(year),
            allowance = allowance
        )
    }

    /**
     * The balance of [entitlement] with [absences] (any; those of other people, workplaces, types
     * or years are left out). A day of two overlapping requests counts once, the approved one first.
     */
    fun balance(entitlement: LeaveEntitlement, absences: List<Absence>): LeaveBalance {
        val rules = PayrollRules.of(entitlement.market)
        val year = entitlement.year
        val leave = absences.filter {
            it.userId == entitlement.userId && it.type in LEAVE_TYPES && it.status != AbsenceStatus.REJECTED &&
                (it.workplaceId == null || it.workplaceId == entitlement.workplaceId)
        }
        // One entry per working day: approved before pending, so a day is never counted twice.
        val byDay = linkedMapOf<LocalDate, Absence>()
        leave.sortedBy { if (it.status == AbsenceStatus.APPROVED) 0 else 1 }.forEach { absence ->
            val start = parse(absence.startDate) ?: return@forEach
            val end = parse(absence.endDate) ?: start
            days(start, end, year).filter { rules.isLeaveDay(it) }.forEach { day -> byDay.getOrPut(day) { absence } }
        }
        val approved = byDay.values.filter { it.status == AbsenceStatus.APPROVED }
        val pending = byDay.values.filter { it.status == AbsenceStatus.PENDING }
        val allowance = entitlement.allowance
        val setByManager = allowance?.entitlementDays != null
        return LeaveBalance(
            userId = entitlement.userId,
            workplaceId = entitlement.workplaceId,
            year = year,
            market = entitlement.market,
            statutoryDays = entitlement.statutoryDays,
            entitlementDays = allowance?.entitlementDays ?: entitlement.statutoryDays ?: 0,
            entitlementSetByManager = setByManager,
            carryOverDays = allowance?.carryOverDays ?: 0,
            usedDays = approved.size,
            pendingDays = pending.size,
            onDemandUsedDays = approved.count { it.type == AbsenceType.ON_DEMAND },
            onDemandPendingDays = pending.count { it.type == AbsenceType.ON_DEMAND },
            onDemandLimit = entitlement.onDemandLimit
        )
    }

    private fun parse(date: String?): LocalDate? =
        date?.takeIf { it.length >= 10 }?.let { runCatching { LocalDate.parse(it.take(10)) }.getOrNull() }
}
